import type { IPointOfSale } from '../shared/models/core/point-of-sale';
import type { IProduct } from '../shared/models/sale/product';
import {
  commitProductSyncStage,
  resetProductSyncStage,
  setOfflineCache,
  stageProductBatch,
} from './db';

type ConnectionInfo = {
  saveData?: boolean;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
};

type NavigatorWithConnection = Navigator & {
  connection?: ConnectionInfo;
  mozConnection?: ConnectionInfo;
  webkitConnection?: ConnectionInfo;
};

type WorkerMessage =
  | {
      type: 'batch';
      syncId: string;
      page: number;
      totalPages: number;
      totalRows: number;
      results: IProduct[];
    }
  | { type: 'complete'; syncId: string; totalPages: number; totalRows: number }
  | { type: 'cancelled'; syncId: string }
  | { type: 'error'; syncId: string; message: string };

export interface ProductBackgroundSyncResult {
  updated: boolean;
  skipped?:
    | 'not-crosland'
    | 'dealer-disabled'
    | 'disabled'
    | 'offline'
    | 'aborted'
    | 'busy'
    | 'save-data'
    | 'slow-network';
  productCount?: number;
}

const getConnection = (): ConnectionInfo | undefined => {
  const nav = navigator as NavigatorWithConnection;
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
};

const getTransferProfile = () => {
  const connection = getConnection();
  const effectiveType = connection?.effectiveType?.toLowerCase();

  if (connection?.saveData) {
    return { allowed: false as const, reason: 'save-data' as const };
  }

  if (effectiveType === 'slow-2g' || effectiveType === '2g') {
    return { allowed: false as const, reason: 'slow-network' as const };
  }

  if (effectiveType === '3g' || (connection?.downlink !== undefined && connection.downlink < 2)) {
    return { allowed: true as const, pageSize: 50, delayMs: 2500 };
  }

  return { allowed: true as const, pageSize: 100, delayMs: 1200 };
};

const waitForBrowserIdle = async (): Promise<void> => {
  if ('requestIdleCallback' in window) {
    await new Promise<void>((resolve) => {
      window.requestIdleCallback(() => resolve(), { timeout: 5000 });
    });
    return;
  }
  await new Promise<void>((resolve) => window.setTimeout(resolve, 750));
};

const runProductSync = async (
  selectedPos: IPointOfSale,
  signal?: AbortSignal
): Promise<ProductBackgroundSyncResult> => {
  if (!selectedPos.isOffline || selectedPos.isDealer) {
    return { updated: false, skipped: selectedPos.isDealer ? 'dealer-disabled' : 'disabled' };
  }
  if (!navigator.onLine) return { updated: false, skipped: 'offline' };

  const transfer = getTransferProfile();
  if (!transfer.allowed) return { updated: false, skipped: transfer.reason };

  await waitForBrowserIdle();
  if (signal?.aborted) return { updated: false, skipped: 'aborted' };
  if (!navigator.onLine) return { updated: false, skipped: 'offline' };

  const syncId = crypto.randomUUID();
  await resetProductSyncStage(selectedPos.companyTaxId, selectedPos.id);
  await setOfflineCache(`productSync:${selectedPos.id}`, {
    status: 'syncing',
    startedAt: new Date().toISOString(),
  });

  const params = new URLSearchParams();
  params.set('stock', '1');
  params.set('isDarkStore', String(!!selectedPos.isDarkStore));
  selectedPos.warehouses.forEach((warehouse) => {
    params.append('marketWarehouseCodes', `${selectedPos.marketCode}:${warehouse.code}`);
  });

  const worker = new Worker('/offline-product-sync-worker.js');
  let writeChain = Promise.resolve();

  return await new Promise<ProductBackgroundSyncResult>((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      signal?.removeEventListener('abort', abortHandler);
      worker.terminate();
    };

    const finish = (result: ProductBackgroundSyncResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const abortHandler = () => {
      worker.postMessage({ type: 'cancel', syncId });
      finish({ updated: false, skipped: 'aborted' });
    };

    signal?.addEventListener('abort', abortHandler, { once: true });

    worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
      const message = event.data;
      if (!message || message.syncId !== syncId) return;

      if (message.type === 'batch') {
        writeChain = writeChain.then(() =>
          stageProductBatch(message.results, selectedPos.companyTaxId, selectedPos.id, syncId)
        );
        return;
      }

      if (message.type === 'complete') {
        void (async () => {
          try {
            await writeChain;
            const productCount = await commitProductSyncStage(
              selectedPos.companyTaxId,
              selectedPos.id,
              syncId
            );
            const completedAt = new Date().toISOString();
            await Promise.all([
              setOfflineCache(`productSync:${selectedPos.id}`, {
                status: 'ready',
                startedAt: completedAt,
                completedAt,
                productCount,
              }),
              setOfflineCache(`sync:${selectedPos.id}`, { lastSyncAt: completedAt }),
            ]);
            finish({ updated: true, productCount });
          } catch (error) {
            fail(error);
          }
        })();
        return;
      }

      if (message.type === 'cancelled') {
        finish({ updated: false, skipped: 'aborted' });
        return;
      }

      fail(new Error(message.message));
    };

    worker.onerror = (event) => {
      fail(new Error(event.message || 'No se pudo actualizar el catalogo offline de productos.'));
    };

    worker.postMessage({
      type: 'sync',
      syncId,
      url: `/api/materials?${params.toString()}`,
      pageSize: transfer.pageSize,
      delayMs: transfer.delayMs,
    });
  });
};

type LockLike = object;
type LockManagerLike = {
  request<T>(
    name: string,
    options: { ifAvailable: boolean },
    callback: (lock: LockLike | null) => Promise<T>
  ): Promise<T>;
};

export const syncCroslandOfflineProducts = async (
  selectedPos: IPointOfSale,
  signal?: AbortSignal
): Promise<ProductBackgroundSyncResult> => {
  const lockManager = (navigator as Navigator & { locks?: LockManagerLike }).locks;
  if (!lockManager) return runProductSync(selectedPos, signal);

  return lockManager.request(
    `epos-offline-products:${selectedPos.companyTaxId}:${selectedPos.id}`,
    { ifAvailable: true },
    async (lock) => {
      if (!lock) return { updated: false, skipped: 'busy' };
      return runProductSync(selectedPos, signal);
    }
  );
};
